import { createFileRoute, Link } from "@tanstack/react-router";
import { Shield, Award, Clock, Heart, CheckCircle2, ShieldCheck, BadgeCheck, Wrench } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SectionDecor } from "@/components/SectionDecor";
import { SiteFooter } from "@/components/SiteFooter";
import { CTABlock } from "@/components/CTABlock";
import { HeroBackground } from "@/components/HeroBackground";
import whyHero from "@/assets/mckay/roof-replacement.webp.asset.json";

export const Route = createFileRoute("/why-choose-us")({
  head: () => ({
    meta: [
      { title: "Why Choose McKay Roofing & Solar – Southern California" },
      { name: "description", content: "Licensed C-39 and C-46 contractors, in-house crews, real warranties. Here's why Southern California homeowners have trusted McKay Roofing & Solar since 1981." },
      { name: "keywords", content: "best roofing contractor Southern California, licensed insured roofer, solar installer Southern California, family owned roofing California, McKay Roofing & Solar" },
      { property: "og:title", content: "Why Choose McKay Roofing & Solar" },
      { property: "og:description", content: "What sets our roofing and solar crew apart across Southern California – since 1981." },
      { property: "og:url", content: "https://mckay.steadyautogrowthdemo.com/why-choose-us" },
    ],
    links: [{ rel: "canonical", href: "https://mckay.steadyautogrowthdemo.com/why-choose-us" }],
  }),
  component: WhyPage,
});

function WhyPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <section className="relative overflow-hidden bg-gradient-hero text-primary-foreground">
          <HeroBackground src={whyHero.url} alt="McKay Roofing & Solar crew installing a new roof in Southern California" />
          <div className="relative mx-auto flex min-h-[85vh] max-w-4xl flex-col justify-center px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-28">
            <h1 className="text-5xl font-extrabold sm:text-6xl text-balance">Why Southern California keeps calling McKay.</h1>
            <p className="mt-5 max-w-2xl text-lg text-primary-foreground/85">There are plenty of roofing and solar companies in Southern California. Here's what we do differently – and why it matters for your home.</p>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="grid gap-6 sm:grid-cols-2">
            {[
              { icon: Shield, t: "Licensed & fully insured", d: "California C-39 roofing and C-46 solar licenses, plus full general liability and workers' comp. We send the COI to your HOA or property manager on request." },
              { icon: Award, t: "Roofing + solar under one roof", d: "We install both – so panels are designed for your actual roof, and re-roofs are coordinated with your solar system instead of tearing it off later." },
              { icon: Clock, t: "Family-owned since 1981", d: "Four decades of Southern California roofs and, more recently, thousands of solar systems. The same family still runs the company today." },
              { icon: Heart, t: "In-house crews, not subcontractors", d: "Every roofer and solar installer on your property is a full-time McKay employee – trained, background-checked and in uniform." },
            ].map((f) => (
              <div key={f.t} className="rounded-2xl border border-border bg-card p-6">
                <f.icon className="mb-3 h-8 w-8 text-primary" />
                <div className="text-xl font-extrabold text-primary">{f.t}</div>
                <p className="mt-2 text-sm text-muted-foreground">{f.d}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 pb-6 sm:px-6 lg:px-8">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <a
              href="https://www.bbb.org/us/ca/spring-valley/profile/electrical-contractors/mckay-roofing-and-solar-1126-1000144420"
              target="_blank"
              rel="noopener noreferrer"
              className="group rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-1 hover:shadow-lg"
            >
              <Award className="h-7 w-7 text-primary" />
              <div className="mt-3 text-base font-bold text-primary">A+ BBB Rating</div>
              <p className="mt-1 text-xs text-muted-foreground">Not currently accredited.</p>
            </a>
            <div className="rounded-2xl border border-border bg-card p-5">
              <ShieldCheck className="h-7 w-7 text-primary" />
              <div className="mt-3 text-base font-bold text-primary">CSLB #1130793</div>
              <p className="mt-1 text-xs text-muted-foreground">California licensed contractor.</p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5">
              <BadgeCheck className="h-7 w-7 text-primary" />
              <div className="mt-3 text-base font-bold text-primary">NRCA Member</div>
              <p className="mt-1 text-xs text-muted-foreground">National Roofing Contractors Association.</p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5">
              <Wrench className="h-7 w-7 text-primary" />
              <div className="mt-3 text-base font-bold text-primary">Manufacturer Systems</div>
              <p className="mt-1 text-xs text-muted-foreground">Owens Corning, IKO, Enphase, Tesla &amp; more.</p>
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden bg-muted/40">
          <SectionDecor variant="shingles" side="left" tone="primary" opacity={0.05} />
          <div className="relative z-10 mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-extrabold text-primary">Our promise to you</h2>
            <ul className="mt-6 space-y-4">
              {[
                "If you're not happy with the workmanship, we come back and make it right. Workmanship warranty on every job.",
                "Free written estimates after a real on-site inspection – never a rushed quote from the driveway.",
                "No high-pressure sales. If a repair is smarter than a replacement, that's what we recommend.",
                "Licensed C-39 and C-46 contractors in California, with full general liability and workers' comp.",
                "If we find something wrong on your roof – flashing, ventilation, decking – we show you photos and explain the options.",
              ].map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-secondary" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 rounded-xl bg-card p-6 text-sm">
              See everything we do on our <Link to="/services/roofing" className="text-accent font-semibold hover:underline">roofing</Link> and <Link to="/services/solar" className="text-accent font-semibold hover:underline">solar</Link> pages, or jump straight to <Link to="/contact" className="text-accent font-semibold hover:underline">contact</Link>.
            </div>
          </div>
        </section>

        <CTABlock />
      </main>
      <SiteFooter />
    </div>
  );
}
